import { db } from "@/integrations/supabase/untyped";

export type FeeAudience =
  "customer" | "business" | "finance_partner" | "dealer";

export interface PlatformFee {
  key: string;
  audience: FeeAudience;
  label: string;
  description: string;
  fee_type: "free" | "flat" | "percent" | "quoted";
  amount: number | null;
  currency: string;
  billing: "one_off" | "monthly" | "per_transaction" | "refundable";
  sort_order: number;
}

/**
 * Shown only if the platform_fees table can't be read. These mirror what is already in
 * effect in the product today (free quotes, deposit after approval, free partner listing).
 */
export const FALLBACK_FEES: PlatformFee[] = [
  {
    key: "quote",
    audience: "customer",
    label: "Import quote",
    description: "Itemised landed-cost quote in GHS within 24 hours.",
    fee_type: "free",
    amount: null,
    currency: "GHS",
    billing: "one_off",
    sort_order: 10,
  },
  {
    key: "commitment_deposit",
    audience: "customer",
    label: "Commitment deposit",
    description:
      "Paid once a quote is approved, to secure sourcing. Set per order and credited to your final invoice.",
    fee_type: "quoted",
    amount: null,
    currency: "GHS",
    billing: "refundable",
    sort_order: 20,
  },
  {
    key: "finance_listing",
    audience: "finance_partner",
    label: "Partner listing",
    description: "Receive consented, pre-qualified financing requests.",
    fee_type: "free",
    amount: null,
    currency: "GHS",
    billing: "one_off",
    sort_order: 70,
  },
];

export async function fetchFees(): Promise<PlatformFee[]> {
  const { data, error } = await db
    .from("platform_fees")
    .select(
      "key, audience, label, description, fee_type, amount, currency, billing, sort_order",
    )
    .eq("is_active", true)
    .order("sort_order", { ascending: true });
  if (error || !data || data.length === 0) return FALLBACK_FEES;
  return (data as PlatformFee[]).map((f) => ({
    ...f,
    amount: f.amount == null ? null : Number(f.amount),
  }));
}

export function formatFeeAmount(
  f: Pick<PlatformFee, "fee_type" | "amount" | "currency" | "billing">,
): string {
  if (f.fee_type === "free") return "Free";
  if (f.fee_type === "quoted" || f.amount == null) return "Quoted per order";
  const value =
    f.fee_type === "percent"
      ? `${f.amount}%`
      : `${f.currency} ${f.amount.toLocaleString("en-GH")}`;
  const suffix =
    f.billing === "monthly"
      ? " / month"
      : f.billing === "per_transaction" && f.fee_type === "percent"
        ? " of order value"
        : "";
  return value + suffix;
}

export const BILLING_NOTE: Record<PlatformFee["billing"], string> = {
  one_off: "One-off",
  monthly: "Billed monthly",
  per_transaction: "Per order",
  refundable: "Credited to your order",
};
